import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, Select, Button } from '@components';
import { CreateAgentData } from '../../types';
import { useApiEndpoints } from '../../hooks/useApiEndpoints';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';
import { EmailTemplate, SMSTemplate } from '../../../Communications/types';
import { RecipientsInput } from './RecipientsInput';
import { ApiEndpointsSection } from './ApiEndpointsSection';

const SHOW_EMAIL_FOLLOW_UP = false;

interface PostCallTabProps {
  formData: CreateAgentData;
  onInputChange: (
    field: keyof CreateAgentData,
    value: string | number | boolean | unknown[]
  ) => void;
  onBatchInputChange?: (updates: Partial<CreateAgentData>) => void;
  errors: Record<string, string>;
}

const PostCallTab: React.FC<PostCallTabProps> = ({
  formData,
  onInputChange,
  onBatchInputChange,
  errors,
}) => {
  const { t } = useTranslation();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [smsTemplates, setSMSTemplates] = useState<SMSTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [openSMSVariableDropdown, setOpenSMSVariableDropdown] = useState(false);
  const smsTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Extract variable names from variable_extraction schema
  const extractedVariables = React.useMemo(() => {
    if (!formData.variable_extraction) return [];

    try {
      const parsed = JSON.parse(formData.variable_extraction);
      if (parsed.schema?.properties) {
        return Object.keys(parsed.schema.properties);
      }
    } catch (e) {
      // Invalid JSON, return empty array
    }
    return [];
  }, [formData.variable_extraction]);

  // Always include post-call default variables, remove duplicates
  const allAvailableVariables = React.useMemo(
    () =>
      Array.from(
        new Set([
          'caller_number',
          'agent_name',
          'started_at',
          'ended_at',
          'conversation_summary',
          ...extractedVariables,
        ])
      ),
    [extractedVariables]
  );

  const {
    apiEndpoints,
    updateApiEndpoint,
    addApiEndpoint,
    removeApiEndpoint,
    addHeader,
    removeHeader,
    updateHeader,
  } = useApiEndpoints({
    formDataCalls: formData.post_api_calls || [],
    callType: 'post',
    onSync: calls => onInputChange('post_api_calls', calls),
  });

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const [emailResponse, smsResponse] = await Promise.all([
          ApiClient.get('/communications/email-templates/'),
          ApiClient.get('/communications/sms-templates/'),
        ]);

        const emailResult = await handleApiResult<EmailTemplate[]>(
          emailResponse,
          'Failed to fetch email templates'
        );
        const smsResult = await handleApiResult<SMSTemplate[]>(
          smsResponse,
          'Failed to fetch SMS templates'
        );

        if (emailResult.success && emailResult.data) {
          setEmailTemplates(emailResult.data.filter(t => t.status === 'active'));
        }
        if (smsResult.success && smsResult.data) {
          setSMSTemplates(smsResult.data.filter(t => t.status === 'active'));
        }
      } catch (err) {
        handleApiCatch(err, 'Failed to fetch templates');
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleEmailTemplateSelect = (templateId: string) => {
    if (!templateId) {
      onInputChange('email_template', '');
      return;
    }

    const template = emailTemplates.find(t => t.id === templateId);
    if (!template) return;

    if (onBatchInputChange) {
      onBatchInputChange({
        email_template: templateId,
        email_follow_up_subject: template.subject || '',
        email_follow_up_content: template.body || '',
      });
    } else {
      onInputChange('email_template', templateId);
      onInputChange('email_follow_up_subject', template.subject || '');
      onInputChange('email_follow_up_content', template.body || '');
    }
  };

  const handleSMSTemplateSelect = (templateId: string) => {
    if (!templateId) {
      onInputChange('sms_template', '');
      return;
    }

    const template = smsTemplates.find(t => t.id === templateId);
    if (!template) return;

    onInputChange('sms_template', templateId);
    onInputChange('sms_follow_up_content', template.content || '');
  };

  const insertSMSVariableAtCursor = (variableName: string) => {
    const textarea = smsTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const variableToken = `{{ ${variableName} }}`;

    const newValue = currentValue.substring(0, start) + variableToken + currentValue.substring(end);

    onInputChange('sms_follow_up_content', newValue);

    // Set cursor position after inserted variable
    setTimeout(() => {
      const newCursorPos = start + variableToken.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);

    setOpenSMSVariableDropdown(false);
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.postCall')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.postCallSettingsDescription')}
        </p>
      </div>

      <div className="space-y-8">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              summarize
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.conversationProcessing')}
            </h4>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="intelligent-conversation-summary"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.intelligentConversationSummary')}
              </label>
              <Select
                id="intelligent-conversation-summary"
                value={formData.intelligent_conversation_summary || 'no'}
                onChange={value =>
                  onInputChange(
                    'intelligent_conversation_summary',
                    Array.isArray(value) ? value[0] : value
                  )
                }
                options={[
                  { value: 'no', label: t('agents.no') },
                  { value: 'yes_with_email', label: t('agents.yesWithEmail') },
                  { value: 'yes_with_sms', label: t('agents.yesWithSms') },
                  { value: 'yes_with_email_and_sms', label: t('agents.yesWithEmailAndSms') },
                ]}
                className="w-full"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('agents.generateAiSummaries')}
              </p>
            </div>

            <div>
              <label
                htmlFor="follow-up-status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.followUpEnabled')}
              </label>
              <Select
                id="follow-up-status"
                value={formData.follow_up_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  const booleanValue = selectedValue === 'enabled';
                  onInputChange('follow_up_enabled', booleanValue);

                  if (!booleanValue && onBatchInputChange) {
                    onBatchInputChange({
                      email_template: null,
                      email_follow_up_subject: '',
                      email_follow_up_content: '',
                      email_follow_up_timing: 'immediately',
                      email_follow_up_recipients: [],
                      sms_template: null,
                      sms_follow_up_content: '',
                      sms_follow_up_timing: 'immediately',
                      sms_follow_up_recipients: [],
                    });
                  }
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
                error={errors.follow_up_enabled ? true : undefined}
                errorText={errors.follow_up_enabled}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('agents.sendAutomatedFollowUp')}
              </p>
            </div>
          </div>
        </div>

        {formData.follow_up_enabled && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                message
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.communicationTemplates')}
              </h4>
            </div>

            <div className="space-y-6">
              {SHOW_EMAIL_FOLLOW_UP && (
                <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center mb-4">
                    <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                      email
                    </span>
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {t('agents.emailFollowUp')}
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="email-template"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t('agents.emailTemplate')}
                      </label>
                      <Select
                        id="email-template"
                        value={formData.email_template || ''}
                        onChange={value =>
                          handleEmailTemplateSelect(Array.isArray(value) ? value[0] : value)
                        }
                        options={[
                          { value: '', label: t('agents.selectEmailTemplate') },
                          ...emailTemplates.map(template => ({
                            value: template.id,
                            label: template.title,
                          })),
                        ]}
                        className="w-full"
                        disabled={templatesLoading}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email-timing"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t('agents.emailSendTiming')}
                      </label>
                      <Select
                        id="email-timing"
                        value={formData.email_follow_up_timing || 'immediately'}
                        onChange={value =>
                          onInputChange(
                            'email_follow_up_timing',
                            Array.isArray(value) ? value[0] : value
                          )
                        }
                        options={[
                          { value: 'immediately', label: t('agents.immediatelyAfterCall') },
                          { value: '1_hour', label: t('agents.1HourAfterCall') },
                          { value: '24_hours', label: t('agents.24HoursAfterCall') },
                          { value: '3_days', label: t('agents.3DaysAfterCall') },
                          { value: '1_week', label: t('agents.1WeekAfterCall') },
                        ]}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label
                      htmlFor="email-subject"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.emailSubject')}
                    </label>
                    <Input
                      id="email-subject"
                      type="text"
                      className="w-full"
                      value={formData.email_follow_up_subject || ''}
                      onChange={e => onInputChange('email_follow_up_subject', e.target.value)}
                      placeholder={t('agents.emailSubjectPlaceholder')}
                    />
                  </div>

                  <div className="mt-6">
                    <label
                      htmlFor="email-content"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.emailContent')}
                    </label>
                    <Textarea
                      id="email-content"
                      className="w-full"
                      rows={6}
                      value={formData.email_follow_up_content || ''}
                      onChange={e => onInputChange('email_follow_up_content', e.target.value)}
                      placeholder={t('agents.emailContentPlaceholder')}
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('agents.useVariableNameForDynamicContent')}
                    </p>
                  </div>

                  <RecipientsInput
                    id="email-recipients"
                    label={t('agents.emailRecipients')}
                    placeholder={t('agents.emailRecipientsHint')}
                    hint={t('agents.emailRecipientsHint')}
                    recipients={formData.email_follow_up_recipients || []}
                    onRecipientsChange={recipients =>
                      onInputChange('email_follow_up_recipients', recipients)
                    }
                    validate={isValidEmail}
                    inputType="email"
                  />
                </div>
              )}

              <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center mb-4">
                  <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                    sms
                  </span>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('agents.smsFollowUp')}
                  </h5>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="sms-template"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.smsTemplate')}
                    </label>
                    <Select
                      id="sms-template"
                      value={formData.sms_template || ''}
                      onChange={value =>
                        handleSMSTemplateSelect(Array.isArray(value) ? value[0] : value)
                      }
                      options={[
                        { value: '', label: t('agents.selectSmsTemplate') },
                        ...smsTemplates.map(template => ({
                          value: template.id,
                          label: template.title,
                        })),
                      ]}
                      className="w-full"
                      disabled={templatesLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="sms-timing"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.smsSendTiming')}
                    </label>
                    <Select
                      id="sms-timing"
                      value={formData.sms_follow_up_timing || 'immediately'}
                      onChange={value =>
                        onInputChange(
                          'sms_follow_up_timing',
                          Array.isArray(value) ? value[0] : value
                        )
                      }
                      options={[
                        { value: 'immediately', label: t('agents.immediatelyAfterCall') },
                        { value: '1_hour', label: t('agents.1HourAfterCall') },
                        { value: '24_hours', label: t('agents.24HoursAfterCall') },
                        { value: '3_days', label: t('agents.3DaysAfterCall') },
                        { value: '1_week', label: t('agents.1WeekAfterCall') },
                      ]}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="sms-content"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {t('agents.smsContent')}
                    </label>
                    {allAvailableVariables.length > 0 && (
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOpenSMSVariableDropdown(!openSMSVariableDropdown)}
                        >
                          <span className="material-icons text-sm mr-1">add_circle</span>
                          {t('agents.insertVariable')}
                        </Button>
                        {openSMSVariableDropdown && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenSMSVariableDropdown(false)}
                              onKeyDown={e =>
                                e.key === 'Escape' && setOpenSMSVariableDropdown(false)
                              }
                              role="button"
                              tabIndex={0}
                              aria-label="Close dropdown"
                            />
                            <div className="absolute right-0 mt-1 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                              <div className="py-1">
                                {allAvailableVariables.map(variable => {
                                  const translationKey = `agents.variables.${variable}`;
                                  const hasTranslation = t(translationKey) !== translationKey;
                                  const displayName = hasTranslation ? t(translationKey) : variable;

                                  return (
                                    <button
                                      key={variable}
                                      onClick={() => insertSMSVariableAtCursor(variable)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                    >
                                      <span>{displayName}</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                        {`{{ ${variable} }}`}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <Textarea
                    ref={el => {
                      smsTextareaRef.current = el;
                    }}
                    id="sms-content"
                    className="w-full"
                    rows={4}
                    maxLength={160}
                    value={formData.sms_follow_up_content || ''}
                    onChange={e => onInputChange('sms_follow_up_content', e.target.value)}
                    placeholder={t('agents.smsContentPlaceholder')}
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('agents.smsLimitedTo160Characters')}
                  </p>
                </div>

                <RecipientsInput
                  id="sms-recipients"
                  label={t('agents.smsRecipients')}
                  placeholder={t('agents.smsRecipientsHint')}
                  hint={t('agents.smsRecipientsHint')}
                  recipients={formData.sms_follow_up_recipients || []}
                  onRecipientsChange={recipients =>
                    onInputChange('sms_follow_up_recipients', recipients)
                  }
                  inputType="tel"
                />
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">api</span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.apiIntegration')}
            </h4>
          </div>

          <div>
            <label
              htmlFor="post-call-api-request"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.postCallApiRequest')}
            </label>
            <Select
              id="post-call-api-request"
              value={formData.post_call_api_request_enabled ? 'enabled' : 'disabled'}
              onChange={value => {
                const selectedValue = Array.isArray(value) ? value[0] : value;
                const booleanValue = selectedValue === 'enabled';
                onInputChange('post_call_api_request_enabled', booleanValue);
              }}
              options={[
                { value: 'disabled', label: t('agents.disabled') },
                { value: 'enabled', label: t('agents.enabled') },
              ]}
              className="w-full"
              error={errors.post_call_api_request_enabled ? true : undefined}
              errorText={errors.post_call_api_request_enabled}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('agents.triggerApiCallsAfterCompletion')}
            </p>
          </div>
        </div>

        {formData.post_call_api_request_enabled && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                webhook
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.apiEndpointsConfiguration')}
              </h4>
            </div>

            <ApiEndpointsSection
              apiEndpoints={apiEndpoints}
              updateApiEndpoint={updateApiEndpoint}
              addApiEndpoint={addApiEndpoint}
              removeApiEndpoint={removeApiEndpoint}
              addHeader={addHeader}
              removeHeader={removeHeader}
              updateHeader={updateHeader}
              allAvailableVariables={allAvailableVariables}
              urlPlaceholder="https://api.example.com/post-call"
              requestBodyPlaceholder={t('agents.requestBodyPlaceholder')}
              includePatch
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { PostCallTab };
